import React, { Context } from 'react';
import { CustomError } from 'ts-custom-error';
import { User } from 'core/types/user';
import { RoleId } from 'core/types/role';

// QueryData<T> gives us a short hand to just pass the response object from the
// useQueryWithPermissions() hook.
interface QueryData<T> {
  data?: {
    permissions: T;
  };
}

function isQueryData<T>(data?: any): data is QueryData<T> {
  if (!data || !data.data || !data.data.permissions) return false;
  return true;
}

export function hasPermission<T>(
  res: QueryData<Record<keyof T, boolean>> | Record<keyof T, boolean>,
  permission: keyof T,
): boolean {
  if (isQueryData(res)) {
    if (!res.data) return false;
    return res.data.permissions[permission];
  }

  return res[permission];
}

export default function createPermissionContext<Permission>(
  initialValue: Permission,
): [() => Permission, Context<Permission>] {
  const PermissionContext = React.createContext<Permission>(initialValue);

  function usePermission(): Permission {
    const context = React.useContext(PermissionContext);
    if (!context)
      throw new Error('usePermission needs to be used within a <Provider>');
    return context;
  }

  return [usePermission, PermissionContext];
}

export function isAdministratorRole(role: RoleId): boolean {
  return (
    role === 'organization:administrator' || role === 'organization:it-manager'
  );
}

/**
 * A simple way to check if the user has an administrator role in the organization.
 *
 * This should not be used so often as we prefer the `permissions` object for each
 * resource instead, but sometimes you just need to know if a user is an admin or not..
 */
export function isAdministrator(user: Pick<User, 'role'>): boolean {
  if (!user.role) return false;
  return isAdministratorRole(user.role.id);
}

export class PermissionError<Permission> extends CustomError {
  attempted: keyof Permission;

  permissions: Permission;

  constructor(attempted: keyof Permission, permissions: Permission) {
    super(
      `User does not have permission to ${attempted} in ${JSON.stringify(permissions)}`,
    );
    this.attempted = attempted;
    this.permissions = permissions;

    Object.defineProperty(this, 'name', { value: 'PermissionError' });
  }
}
